/**
 * Requêtes API pour les templates web (admin).
 * Utilise l'instance Axios partagée (baseURL = REACT_APP_API_URL, JWT auto-injecté).
 */
import { instance } from '../../requests/axios.instance';

const BASE = '/proposals/web_templates';

/** Liste les templates (sans puck_data). `filters` : `kind`, `templatable`,
 *  `work` — la création d'un site ne propose que les modèles qui lui vont. */
export async function fetchWebTemplates(filters = {}) {
  const res = await instance.get(BASE, { params: filters });
  return res.data;
}

/** Métier d'une opportunité : il borne les modèles proposés. Une offre n'en a pas. */
async function documentWork(documentIdentity) {
  if (documentIdentity?.type !== 'opportunity') return null;
  const res = await instance.get(`/proposals/opportunities/${documentIdentity.id}/work`);
  return res.data?.name || null;
}

/** Modèles de cet usage (`kind`) qui conviennent au document : son type, et son métier. */
export async function fetchWebTemplatesForDocument(documentIdentity, kind) {
  const work = await documentWork(documentIdentity);
  return fetchWebTemplates({
    kind,
    templatable: documentIdentity?.type,
    ...(work ? { work } : {}),
  });
}

/** Charge un template complet par ID */
export async function fetchWebTemplate(id) {
  const res = await instance.get(`${BASE}/${id}`);
  return res.data;
}

/** Crée un nouveau template */
export async function createWebTemplate(data) {
  const res = await instance.post(BASE, { web_template: data });
  return res.data;
}

/** Met à jour un template existant. `version` = jeton de conflit (cf. saveWebData). */
export async function updateWebTemplate(id, data, version) {
  const res = await instance.patch(`${BASE}/${id}`, {
    web_template: data,
    ...(version != null ? { version } : {}),
  });
  return res.data;
}

/** Renomme depuis la liste, qui n'a pas le jeton de conflit : on le relit juste
 *  avant (un 409 reste possible si quelqu'un enregistre entre les deux). */
export async function renameWebTemplate(id, name) {
  const { version } = await fetchWebTemplate(id);
  return updateWebTemplate(id, { name }, version);
}

/** Écriture granulaire : `scope` = "page:<slug>" ou "header" | "footer" |
 *  "login" | "loading". `version` = jeton DE CE SCOPE. */
export async function patchWebTemplateBlocks(id, { scope, diff, version }) {
  const res = await instance.patch(`${BASE}/${id}/blocks`, {
    scope,
    diff,
    ...(version != null ? { version } : {}),
  });
  return res.data;
}

/** Structure du template : pages statiques, ordre, thème, nom. */
export async function patchWebTemplateStructure(id, { diff, version }) {
  const res = await instance.patch(`${BASE}/${id}/structure`, {
    diff,
    ...(version != null ? { version } : {}),
  });
  return res.data;
}

/** Duplique un template */
export async function duplicateWebTemplate(id) {
  const res = await instance.post(`${BASE}/${id}/duplicate`);
  return res.data;
}

/** Supprime un template (suppression logique côté backend).
 *  Le serveur refuse (409) tant que quelqu'un l'édite : on remonte les noms
 *  plutôt qu'un « Request failed with status code 409 ». */
export async function deleteWebTemplate(id) {
  try {
    await instance.delete(`${BASE}/${id}`);
  } catch (e) {
    const data = e.response?.status === 409 ? e.response.data : null;
    if (data?.error !== 'template_in_use') throw e;
    const names = (data.editors || []).map((editor) => editor.name).filter(Boolean);
    throw new Error(names.length
      ? `Ce template est en cours d'édition par ${names.join(', ')}.`
      : 'Ce template est en cours d\'édition.');
  }
}
